"use client";

import { useState } from "react";
import Image from "next/image";
import type { SbBlokData } from "@storyblok/react/rsc";
import { storyblokEditable } from "@/lib/storyblok-editable";

import { headingOn, surfaceFrom } from "@/components/optilase/surface";
import { cn } from "@/lib/utils";
import { useMediaQuery } from "@/lib/use-media-query";
import { headingLines } from "@/lib/heading-lines";

type Asset = { filename?: string; alt?: string };

type AccordionLogoItem = SbBlokData & {
  logo?: Asset;
  title?: string;
  description?: string;
};

type AccordionListWithLogoBlok = SbBlokData & {
  title?: string;
  items?: AccordionLogoItem[];
  bg_color?: unknown;
};

function ChevronIcon({ open }: { open: boolean }) {
  return (
    <svg
      width="22"
      height="22"
      viewBox="0 0 20 20"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden
      className={`transition-transform duration-200 ease-out ${open ? "rotate-90" : ""}`}
    >
      <path
        d="M7.5 5l5 5-5 5"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export default function AccordionListWithLogo({ blok }: { blok: AccordionListWithLogoBlok }) {
  const items = blok.items || [];
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const surface = surfaceFrom(blok.bg_color);

  const isDesktop = useMediaQuery("(min-width: 768px)");

  if (items.length === 0) return null;

  return (
    <section
      {...storyblokEditable(blok)}
      style={surface ? { backgroundColor: surface.backgroundColor } : undefined}
      className={cn("px-5 py-10 md:px-10 md:py-10 lg:px-20 lg:py-20", surface ? "" : "bg-teal-01")}
    >
      <div className="mx-auto w-full max-w-7xl">
        {blok.title && (
          <h2 className={cn("text-section max-w-[1024px]", headingOn(surface, "text-teal-08"))}>
            {headingLines(blok.title)}
          </h2>
        )}

        <ul className="mt-8 flex flex-col gap-3 md:gap-4 lg:mt-12 lg:gap-6">
          {items.map((item, i) => {
            const open = openIndex === i;
            const hasDescription = Boolean(item.description);
            const logo = item.logo?.filename ? (
              <Image
                src={item.logo.filename}
                alt={item.logo.alt || ""}
                width={360}
                height={360}
                className={
                  isDesktop
                    ? "size-[100px] shrink-0 object-contain lg:size-[130px]"
                    : "size-16 shrink-0 object-contain"
                }
              />
            ) : null;

            return (
              <li
                key={item._uid}
                {...storyblokEditable(item)}
                className="rounded-xl bg-white px-5 py-6 md:p-6 lg:px-8 lg:py-10"
              >
                {isDesktop ? (
                  <div className="flex items-start gap-5 lg:gap-12">
                    {logo}
                    <div className="flex min-w-0 flex-1 flex-col gap-4 pt-2">
                      <span className="text-h5 text-grey-08 leading-[1.08] font-bold">
                        {item.title}
                      </span>
                      {hasDescription && (
                        <p className="text-grey-06 text-base leading-[1.56] lg:text-xl">
                          {item.description}
                        </p>
                      )}
                    </div>
                  </div>
                ) : (
                  <>
                    <button
                      type="button"
                      onClick={() => hasDescription && setOpenIndex(open ? null : i)}
                      aria-expanded={hasDescription ? open : undefined}
                      disabled={!hasDescription}
                      className="flex w-full cursor-pointer items-center gap-4 text-left"
                    >
                      {logo}
                      <span className="text-h5 text-grey-08 flex-1 leading-tight font-bold">
                        {item.title}
                      </span>
                      {hasDescription && (
                        <span className="text-teal-07 flex size-5 shrink-0 items-center justify-center">
                          <ChevronIcon open={open} />
                        </span>
                      )}
                    </button>

                    {hasDescription && (
                      <div
                        inert={!open}
                        className={`grid transition-[grid-template-rows] duration-300 ease-out ${
                          open ? "grid-rows-[1fr]" : "grid-rows-[0fr]"
                        }`}
                      >
                        <div className="overflow-hidden">
                          <p className="text-grey-06 pt-4 text-base leading-relaxed tracking-[-0.01em]">
                            {item.description}
                          </p>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </li>
            );
          })}
        </ul>
      </div>
    </section>
  );
}
